// @ts-nocheck
/**
 * tableRenderer.ts — Template V2 table component renderer.
 *
 * Modes: DEC-077 list/matrix `table.source`, or CTBL/fileTable `table.tableSource`.
 */

import type { TablePropertySchema } from '../../types/category';
import type { ValueSource } from '../../types/binding';
import { decodeAllRowsAllowingItemRefInt } from '../table/tableCodec';
import { looksLikeFileTableManifestValue } from '../table/fileTableManifest';
import {
  previewRowsFromListTable,
  rowsFromListValue,
} from '../table/listTableCoercion';
import { applyExpressionFallback, buildDescriptor, resolveDisplayString, resolveValue } from './rendererUtils';
import { formatFrozenTableCell } from './viewingPaintHelpers';
import { componentInteractionProps } from './interactionProps';
import type { CompiledPropsByType, ComponentRenderer } from './types';
import { extractTypedProps } from './types';
import { migrateTablePayloadInPlace } from './tablePayloadMigration';

export type TableStorageKind = 'ctbl' | 'fileTable' | 'list';

function resolveTableBindingMeta(
  context: Parameters<ComponentRenderer<CompiledPropsByType['table']>['render']>[0],
  inlineSchema: TablePropertySchema | undefined,
  tableSource: ValueSource<string> | undefined,
): {
  schema: TablePropertySchema | undefined;
  storageKind: 'ctbl' | 'fileTable';
  propertyId?: string;
} {
  if (tableSource?.kind === 'binding' && tableSource.ref.kind === 'categoryProperty') {
    const ref = tableSource.ref;
    const category = context.compiledTemplate.categoriesById[ref.categoryId];
    const property = category?.properties.find((prop) => prop.id === ref.propertyId);
    const schema =
      inlineSchema ?? (property?.validation?.tableSchema as TablePropertySchema | undefined);
    const storageKind = property?.type === 'fileTable' ? 'fileTable' : 'ctbl';
    return { schema, storageKind, propertyId: ref.propertyId };
  }
  return {
    schema: inlineSchema,
    storageKind: 'ctbl',
  };
}

function isListBackedBindingSource(
  source: CompiledPropsByType['table']['table']['source'] | undefined,
): boolean {
  if (!source || source.kind !== 'binding') return false;
  return source.ref.kind === 'localState' || source.ref.kind === 'itemVariable';
}

export const TABLE_RENDERER: ComponentRenderer<CompiledPropsByType['table']> = {
  supportedType: 'table',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const extracted = extractTypedProps('table', context.node.props);
    const tablePayload = { ...(extracted.table as unknown as Record<string, unknown>) };
    migrateTablePayloadInPlace(tablePayload);
    const p = { table: tablePayload as CompiledPropsByType['table']['table'] };

    const listSourceRaw = p.table.source != null ? resolveValue(p.table.source, context) : undefined;
    const useListBackedSource = Array.isArray(listSourceRaw);

    // List-backed: localState / itemVariable arrays — first row = headers (ignore schema).
    if (useListBackedSource) {
      if (p.table.tableSource != null) {
        context.issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: `Table node "${context.node.id}": list-backed table.source is set; tableSource data bind is ignored.`,
          path: `${context.node.id}.table.source`,
        });
      }
      const coerced = rowsFromListValue(listSourceRaw);
      for (const issue of coerced.issues) {
        context.issues.push({
          severity: issue.severity,
          code: 'schema.invalid_field_type',
          message: `Table node "${context.node.id}": ${issue.message}`,
          path: context.node.id,
        });
      }
      const limit = p.table.visibleRowCount;
      const limitedRows =
        limit != null && limit >= 0 ? coerced.rows.slice(-limit) : coerced.rows;
      const previewRows = previewRowsFromListTable(limitedRows);
      const editableRequested = p.table.editable === true;
      if (editableRequested && isListBackedBindingSource(p.table.source)) {
        context.issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: `Table node "${context.node.id}": list-backed localState/itemVariable tables are read-only; editable is ignored.`,
          path: context.node.id,
        });
      }
      return buildDescriptor(
        context.node,
        visible,
        enabled,
        {
          base64: '',
          storedValue: '',
          storageKind: 'list' as TableStorageKind,
          schema: undefined,
          rows: limitedRows,
          previewRows,
          rowCount: limitedRows.length,
          columnHeaders: coerced.columnHeaders,
          visibleRowCount: p.table.visibleRowCount,
          editable: false,
          ...componentInteractionProps(context.node),
        },
        { diagnostics: context.issues.slice(issuesBefore), renderContext: context },
      );
    }

    // Authored list/localState/itemVariable source that did not resolve to an array:
    // never fall through to category tableSource schema headers.
    if (isListBackedBindingSource(p.table.source)) {
      if (p.table.tableSource != null) {
        context.issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: `Table node "${context.node.id}": list-backed table.source did not resolve to an array; tableSource data bind is ignored.`,
          path: `${context.node.id}.table.source`,
        });
      }
      return buildDescriptor(
        context.node,
        visible,
        enabled,
        {
          base64: '',
          storedValue: '',
          storageKind: 'list' as TableStorageKind,
          schema: undefined,
          rows: [],
          previewRows: [],
          rowCount: 0,
          columnHeaders: [],
          visibleRowCount: p.table.visibleRowCount,
          editable: false,
          ...componentInteractionProps(context.node),
        },
        { diagnostics: context.issues.slice(issuesBefore), renderContext: context },
      );
    }

    const tableSource = p.table.tableSource;
    const raw = tableSource != null ? resolveValue(tableSource, context) : undefined;
    const storedValue = typeof raw === 'string' ? raw : '';

    const meta = resolveTableBindingMeta(context, p.table.schema, tableSource);
    const schema = meta.schema;
    const storageKind: TableStorageKind =
      meta.storageKind === 'fileTable' || looksLikeFileTableManifestValue(storedValue)
        ? 'fileTable'
        : 'ctbl';

    const rows: Array<{ values: readonly (string | number | null)[] }> = [];
    if (schema && storedValue && storageKind === 'ctbl') {
      const decoded = decodeAllRowsAllowingItemRefInt(storedValue, schema);
      if (decoded.ok) {
        const limit = p.table.visibleRowCount ?? decoded.value.length;
        rows.push(...decoded.value.slice(-limit));
      } else {
        context.issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: `Table blob could not be decoded for node "${context.node.id}": ${decoded.message}`,
          path: context.node.id,
        });
      }
    }

    const columnHeaders =
      schema?.columns.map((col) => {
        return resolveDisplayString(col.headerName, context.locale, col.id);
      }) ?? [];

    const previewRows = rows.map((row) =>
      row.values.map((c, index) => formatFrozenTableCell(c, schema?.columns[index]?.type)),
    );

    return buildDescriptor(
      context.node,
      visible,
      enabled,
      {
        base64: storedValue,
        storedValue,
        storageKind,
        propertyId: meta.propertyId,
        schema,
        rows,
        previewRows,
        rowCount: rows.length,
        columnHeaders,
        visibleRowCount: p.table.visibleRowCount,
        editable: p.table.editable ?? false,
        ...componentInteractionProps(context.node),
      },
      { diagnostics: context.issues.slice(issuesBefore), renderContext: context },
    );
  },
};
